import {
  PROTOCOL_VERSION,
  decodeServerFrame,
  encodeAudioFrame,
  encodeClientMessage,
  recoveryFor,
} from '@namu/protocol'
import type {
  AudioFormat,
  ClientMessage,
  ErrorCode,
  Locale,
  ProfileContext,
  ServerMessage,
  SessionLimits,
} from '@namu/protocol'

/**
 * The protocol, over a socket, and nothing else.
 *
 * Deliberately knows nothing about microphones, speakers or React. It takes
 * bytes in and gives events out, which is what makes it the one part of this
 * feature that can be tested properly: the audio pieces need hardware that no
 * test environment has, and this needs a fake socket.
 *
 * Reconnection lives here too, because the decision about whether to reconnect
 * depends on why the connection ended, and that reason arrives on this socket.
 * A client that reconnects on every close hammers a server that is trying to
 * tell it to stop.
 */

export interface RealtimeHandlers {
  ready(event: {
    sessionId: string
    resumed: boolean
    output: AudioFormat
    limits: SessionLimits
    model: { id: string; backed: boolean }
  }): void
  state(state: 'idle' | 'listening' | 'thinking' | 'speaking', turn: number): void
  transcript(event: { role: 'user' | 'assistant'; text: string; final: boolean; turn: number }): void
  speechBegin(turn: number): void
  audio(turn: number, payload: Uint8Array): void
  speechEnd(event: { turn: number; interrupted: boolean }): void
  /** The model asked to remember something. A request, never applied by itself. */
  patch(profile: ProfileContext): void
  failed(event: { code: ErrorCode; message: string; fatal: boolean }): void
  /** The connection dropped. `willRetry` says whether this client is handling it. */
  disconnected(event: { code: number; willRetry: boolean }): void
  closed(): void
}

/** Anything shaped like a WebSocket. Injected so tests need no network. */
export interface SocketLike {
  readonly readyState: number
  binaryType: string
  send(data: string | ArrayBufferLike | ArrayBufferView): void
  close(code?: number, reason?: string): void
  onopen: ((event: unknown) => void) | null
  onmessage: ((event: { data: unknown }) => void) | null
  onerror: ((event: unknown) => void) | null
  onclose: ((event: { code: number; reason: string }) => void) | null
}

export type SocketFactory = (url: string) => SocketLike

export interface RealtimeClientOptions {
  url: string
  /** Called before every connection attempt, so a stale token is never used. */
  token(): Promise<string>
  locale: Locale
  input: AudioFormat
  output: AudioFormat
  /**
   * Which persona should answer.
   *
   * Read on every connection rather than captured once, because the picker in
   * Settings can move it while a conversation is open and a reconnect must not
   * quietly restore the one the person just changed away from.
   */
  persona?: () => string
  profile?: ProfileContext | undefined
  /** Ask the assistant to speak first, rather than waiting to be spoken to. */
  greet?: boolean
  handlers: RealtimeHandlers
  socketFactory?: SocketFactory
  /** How long to wait for `ready` before giving up on an attempt. */
  handshakeTimeoutMs?: number
  maxRetries?: number
  now?: () => number
}

const OPEN = 1

/**
 * Backoff for reconnection.
 *
 * Exponential with a ceiling, and jittered. The jitter is the part that is
 * easy to leave out and matters most: when a server restarts, every client
 * that was connected reconnects at once, and without jitter they all come back
 * in the same millisecond and knock it over again.
 */
function backoffMs(attempt: number, random: () => number): number {
  const base = Math.min(500 * 2 ** attempt, 8_000)
  return Math.round(base * (0.5 + random() * 0.5))
}

export interface RealtimeClient {
  connect(): Promise<void>
  /** Audio for the current utterance. The turn number groups a run of frames. */
  sendAudio(turn: number, payload: Uint8Array): void
  startTurn(): void
  endTurn(): void
  interrupt(): void
  setMuted(muted: boolean): void
  setHeld(held: boolean): void
  updateProfile(profile: ProfileContext): void
  /** Change who answers, from the next turn. Ignored while disconnected. */
  setPersona(persona: string): void
  ping(): void
  /** Ends the conversation and stops reconnecting. */
  close(reason?: string): void
  readonly connected: boolean
  readonly sessionId: string | null
  /** Round trip in milliseconds, from the last pong. Null until one arrives. */
  readonly latencyMs: number | null
}

export function createRealtimeClient(options: RealtimeClientOptions): RealtimeClient {
  const {
    url,
    token,
    locale,
    input,
    output,
    persona,
    profile,
    greet = true,
    handlers,
    socketFactory = defaultSocketFactory,
    handshakeTimeoutMs = 15_000,
    maxRetries = 6,
    now = Date.now,
  } = options

  let socket: SocketLike | null = null
  let sessionId: string | null = null
  let lastEventId = 0
  let ready = false
  let closedByUs = false
  let attempt = 0
  let latencyMs: number | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let handshakeTimer: ReturnType<typeof setTimeout> | null = null

  function send(message: ClientMessage): void {
    if (!socket || socket.readyState !== OPEN) return
    socket.send(encodeClientMessage(message))
  }

  function clearTimers(): void {
    if (retryTimer) clearTimeout(retryTimer)
    if (handshakeTimer) clearTimeout(handshakeTimer)
    retryTimer = null
    handshakeTimer = null
  }

  async function open(): Promise<void> {
    const accessToken = await token()
    const next = socketFactory(url)
    next.binaryType = 'arraybuffer'
    socket = next

    handshakeTimer = setTimeout(() => {
      if (ready) return
      // A socket that opened and then said nothing is worse than one that
      // failed to open, because nothing will ever wake it up.
      next.close(4000, 'handshake timeout')
    }, handshakeTimeoutMs)

    next.onopen = () => {
      const chosen = persona?.()
      const hello: ClientMessage = {
        type: 'hello',
        protocol: PROTOCOL_VERSION,
        token: accessToken,
        locale,
        input,
        output,
        greet,
        // Omitted rather than sent empty when nothing was chosen: the server
        // reads an absent persona as "use your own default", which is a
        // different request from asking for one by name.
        ...(chosen === undefined ? {} : { persona: chosen }),
        ...(profile === undefined ? {} : { profile }),
        // Only offered when there is something to resume. A first connection
        // sending a resume block would be asking the server to look up a
        // session that has never existed.
        ...(sessionId === null ? {} : { resume: { sessionId, lastEventId } }),
        client: { name: 'namu-app', version: APP_VERSION },
      }
      next.send(encodeClientMessage(hello))
    }

    next.onmessage = (event) => {
      const frame = decodeServerFrame(event.data as string | ArrayBuffer)
      if (frame.kind === 'invalid') {
        // Its own server sent something it cannot read. Worth reporting rather
        // than silently ignoring, because it means a version mismatch.
        handlers.failed({ code: 'bad_message', message: frame.reason, fatal: false })
        return
      }
      if (frame.kind === 'audio') {
        handlers.audio(frame.frame.turn, frame.frame.payload)
        return
      }
      receive(frame.message)
    }

    next.onerror = () => {
      // Browsers deliberately give no detail here, to avoid leaking whether a
      // host exists. The close event that follows is the useful one.
    }

    next.onclose = (event) => {
      clearTimers()
      const wasReady = ready
      ready = false
      socket = null

      if (closedByUs) {
        handlers.closed()
        return
      }

      const willRetry = attempt < maxRetries
      handlers.disconnected({ code: event.code, willRetry })
      if (!willRetry) {
        handlers.closed()
        return
      }

      // A connection that never got as far as `ready` is a different problem
      // from one that dropped mid-conversation, and only the second is worth
      // resuming into.
      if (!wasReady) sessionId = null

      const wait = backoffMs(attempt, Math.random)
      attempt += 1
      retryTimer = setTimeout(() => void open(), wait)
    }
  }

  function receive(message: ServerMessage): void {
    lastEventId = Math.max(lastEventId, message.id)

    switch (message.type) {
      case 'ready': {
        ready = true
        attempt = 0
        sessionId = message.sessionId
        if (handshakeTimer) clearTimeout(handshakeTimer)
        handshakeTimer = null
        handlers.ready({
          sessionId: message.sessionId,
          resumed: message.resumed,
          output: message.output as AudioFormat,
          limits: message.limits,
          model: message.model,
        })
        return
      }
      case 'state':
        handlers.state(message.state, message.turn)
        return
      case 'transcript':
        handlers.transcript({
          role: message.role,
          text: message.text,
          final: message.final,
          turn: message.turn,
        })
        return
      case 'speech_begin':
        handlers.speechBegin(message.turn)
        return
      case 'speech_end':
        handlers.speechEnd({ turn: message.turn, interrupted: message.interrupted })
        return
      case 'patch':
        handlers.patch(message.profile)
        return
      case 'pong':
        latencyMs = now() - message.at
        return
      case 'error': {
        const fatal = recoveryFor(message.code) === 'fatal'
        handlers.failed({ code: message.code, message: message.message, fatal })
        return
      }
      case 'closing': {
        /*
         * The server is hanging up and has said why. `going_away` is a restart
         * and is worth reconnecting into; anything else is deliberate and is
         * not. Without this, a rolling deploy is indistinguishable from being
         * thrown out, and the client either gives up when it should retry or
         * hammers a server that has already refused it.
         */
        if (message.code !== 'going_away') {
          closedByUs = true
          sessionId = null
        }
        return
      }
    }
  }

  return {
    async connect() {
      closedByUs = false
      attempt = 0
      await open()
    },

    sendAudio(turn, payload) {
      if (!socket || socket.readyState !== OPEN || !ready) return
      socket.send(encodeAudioFrame({ kind: 'speech', turn, payload }))
    },

    startTurn: () => send({ type: 'start_turn' }),
    endTurn: () => send({ type: 'end_turn' }),
    interrupt: () => send({ type: 'interrupt' }),
    setMuted: (muted) => send({ type: 'mute', muted }),
    setHeld: (held) => send({ type: 'hold', held }),
    updateProfile: (next) => send({ type: 'context', profile: next }),

    /*
     * Persona alone, with no profile beside it.
     *
     * `context` applies each field only when it is present, which is what
     * makes this safe: a frame carrying the profile as well would restate
     * facts the session already holds, and one carrying an empty profile would
     * blank them. The server applies it from the next turn, so whatever is
     * being spoken now finishes in the voice it started in.
     */
    setPersona: (next) => send({ type: 'context', persona: next }),
    ping: () => send({ type: 'ping', at: now() }),

    close(reason) {
      closedByUs = true
      clearTimers()
      send({ type: 'bye', ...(reason === undefined ? {} : { reason }) })
      socket?.close(1000, 'done')
      socket = null
      ready = false
    },

    get connected() {
      return ready && socket?.readyState === OPEN
    },
    get sessionId() {
      return sessionId
    },
    get latencyMs() {
      return latencyMs
    },
  }
}

const APP_VERSION = '0.1.0'

function defaultSocketFactory(url: string): SocketLike {
  return new WebSocket(url) as unknown as SocketLike
}
